import React, { Suspense, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import extract from 'helpers/extract';
import {
  mutationAppCookiesConsent,
  mutationAppRefresh,
} from 'gql/mutations/app';
import { useQueryLocalApp } from 'gql/queries/local';
import { useQueryMe } from 'gql/queries/me';
import useSiteContext from 'hooks/useSiteContext';

const CookieConsent = React.lazy(() => import('./CookieConsent'));

const CookieConsentContainer = () => {
  const { t } = useTranslation();
  const { isGAR, isInNative } = useSiteContext();
  const {
    data: {
      app: {
        isAuthenticated,
        cookieConsentBasic,
        cookieConsentFunctionality,
        cookieConsentPerformance,
        cookieConsentTracking,
      },
    },
  } = useQueryLocalApp();
  useEffect(() => {
    if (cookieConsentBasic && !isGAR && !isInNative) {
      (function gtm(w, d, s, l, i) {
        // eslint-disable-next-line no-param-reassign
        w[l] = w[l] || [];
        w[l].push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
        w[l].push({ cookieFunctionality: cookieConsentFunctionality });
        w[l].push({ cookiePerformance: cookieConsentPerformance });
        w[l].push({ cookieTracking: cookieConsentTracking });
        const f = d.getElementsByTagName(s)[0];
        const j = d.createElement(s);
        const dl = l !== 'dataLayer' ? `&l=${l}` : '';
        j.async = true;
        j.src = `https://www.googletagmanager.com/gtm.js?id=${i}${dl}`;
        f.parentNode.insertBefore(j, f);
      })(window, document, 'script', 'dataLayer', process.env.REACT_APP_GTM_ID);
    }
  }, [
    cookieConsentBasic,
    cookieConsentFunctionality,
    cookieConsentPerformance,
    cookieConsentTracking,
    isGAR,
    isInNative,
  ]);
  const { isLoading, data } = useQueryMe({
    skip: !isAuthenticated,
  });
  useEffect(() => {
    if (cookieConsentBasic && !isLoading && !isGAR && !isInNative) {
      window.CRISP_READY_TRIGGER = () => {
        window.$crisp.push(['safe', true]);
        window.$crisp.push([
          'set',
          'user:nickname',
          [`${data.me.firstName} ${data.me.lastName}`],
        ]);
        window.$crisp.push(['set', 'user:email', [data.me.email]]);
        window.$crisp.push(['set', 'user:avatar', [data.me.picture]]);
        window.$crisp.push(['set', 'user:company', [data.me.institution]]);
        window.$crisp.push([
          'set',
          'session:segments',
          [
            [
              `language-${data.me.language}`,
              `sector-${data.me.sector}`,
              `role-${data.me.role}`,
              `marketing-${data.me.wantsMarketing ? 'on' : 'off'}`,
              ...data.me.tags,
            ],
          ],
        ]);
        window.$crisp.push([
          'set',
          'session:data',
          [
            [
              ['id', data.me.id],
              ['firstName', data.me.firstName],
              ['lastName', data.me.lastName],
              ['username', data.me.username],
              ['email', data.me.email],
              ['language', data.me.language],
              ['sector', data.me.sector],
              ['role', data.me.role],
              ['institution', data.me.institution],
              ['branch', data.me.branch],
            ],
          ],
        ]);
        if (data.me.role !== 'teacher') {
          window.$crisp.push(['do', 'chat:hide']);
        }
      };
      if (window.Sentry) {
        window.Sentry.setUser({
          id: data.me.id,
        });
      }
    }
  }, [data, isGAR, isInNative, isLoading, cookieConsentBasic]);
  const setCookieConsent = useCallback((values) => {
    const consentCookies = extract(
      values,
      yup.object().shape({
        cookieConsentFunctionality: yup.boolean(),
        cookieConsentPerformance: yup.boolean(),
        cookieConsentTracking: yup.boolean(),
      }),
    );
    mutationAppCookiesConsent({
      cookieConsentBasic: true,
      ...consentCookies,
    });
    mutationAppRefresh();
  }, []);
  const handleAcceptAll = useCallback(() => {
    setCookieConsent({
      cookieConsentFunctionality: true,
      cookieConsentPerformance: true,
      cookieConsentTracking: true,
    });
  }, [setCookieConsent]);
  const handleAcceptEssential = useCallback(() => {
    setCookieConsent({
      cookieConsentFunctionality: false,
      cookieConsentPerformance: false,
      cookieConsentTracking: false,
    });
  }, [setCookieConsent]);
  const [hasCustomized, setHasCustomized] = useState(false);
  const handleBack = useCallback(() => setHasCustomized(false), []);
  const handleCustomize = useCallback(() => setHasCustomized(true), []);
  return cookieConsentBasic || isGAR || isInNative ? null : (
    <Suspense fallback={null}>
      <CookieConsent
        handleAcceptAll={handleAcceptAll}
        handleBack={handleBack}
        handleCustomizationSave={setCookieConsent}
        handleCustomize={handleCustomize}
        handleAcceptEssential={handleAcceptEssential}
        hasCustomized={hasCustomized}
        t={t}
      />
    </Suspense>
  );
};

export default CookieConsentContainer;
